import React, { useMemo, useState } from "react";
import { DashboardFilters, DashboardFiltersBar } from "../../components/DashboardFiltersBar";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";

type AuditLogItem = {
  id: number;
  event: string;
  userId?: number | null;
  user?: { id: number; name: string; email: string } | null;
  entityId?: number | null;
  resourceType?: string | null;
  resourceId?: string | null;
  before?: unknown;
  after?: unknown;
  createdAt: string;
};

type AuditFilters = {
  event?: string;
  userId?: string;
  resourceType?: string;
  resourceId?: string;
  q?: string;
};

export const AuditLogsPage: React.FC = () => {
  const { client } = useAuth();
  const [globalFilters, setGlobalFilters] = useState<DashboardFilters>({});
  const [filters, setFilters] = useState<AuditFilters>({});
  const [selected, setSelected] = useState<AuditLogItem | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listFilters = useMemo(() => {
    const userIdValue = filters.userId ? Number(filters.userId) : undefined;
    const userId = Number.isNaN(userIdValue) ? undefined : userIdValue;
    return {
      event: filters.event,
      userId,
      resourceType: filters.resourceType,
      resourceId: filters.resourceId,
      q: filters.q,
      entityId: globalFilters.entityId ?? undefined,
      from: globalFilters.from,
      to: globalFilters.to,
    };
  }, [filters, globalFilters]);

  const { rows, total, loading, error: listError, page, take, setPage } =
    usePagedQuery<AuditLogItem>({
      endpoint: "/audit/logs",
      filters: listFilters,
    });

  const formatList = (value?: unknown) => {
    if (!value) return "-";
    if (Array.isArray(value)) {
      return value.length ? value.join(", ") : "-";
    }
    return String(value);
  };

  const selectedAfter =
    selected?.after && typeof selected.after === "object" && !Array.isArray(selected.after)
      ? (selected.after as Record<string, unknown>)
      : null;
  const selectedMail =
    selectedAfter && typeof selectedAfter.mail === "object"
      ? (selectedAfter.mail as Record<string, unknown>)
      : null;
  const selectedMailError =
    selectedMail && typeof selectedMail.error === "object"
      ? (selectedMail.error as Record<string, unknown>)
      : null;

  const handleExport = async () => {
    setError(null);
    setExporting(true);
    const params = new URLSearchParams();
    Object.entries(listFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
    params.set("limit", "1000");
    const response = await client.get(`/audit/logs/export?${params.toString()}`);
    if (!response.ok) {
      setError("Falha ao exportar.");
      console.error("Audit export failed", { status: response.status });
      setExporting(false);
      return;
    }
    const csv = await response.text();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "audit-logs.csv";
    link.click();
    window.URL.revokeObjectURL(url);
    setExporting(false);
  };

  return (
    <div className="container py-4">
      <h1 className="mb-3">Auditoria</h1>
      <DashboardFiltersBar value={globalFilters} onChange={setGlobalFilters} />

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Evento</label>
              <input
                className="form-control"
                value={filters.event ?? ""}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, event: event.target.value }))
                }
                placeholder="FIN_PAYABLE_CREATE"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">User ID</label>
              <input
                className="form-control"
                value={filters.userId ?? ""}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, userId: event.target.value }))
                }
                placeholder="Id do usuario"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Resource Type</label>
              <input
                className="form-control"
                value={filters.resourceType ?? ""}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, resourceType: event.target.value }))
                }
                placeholder="AccountPayable"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Resource ID</label>
              <input
                className="form-control"
                value={filters.resourceId ?? ""}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, resourceId: event.target.value }))
                }
                placeholder="Id do recurso"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Busca</label>
              <input
                className="form-control"
                value={filters.q ?? ""}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, q: event.target.value }))
                }
                placeholder="Busca por evento ou recurso"
              />
            </div>
            <div className="col-md-6 d-flex align-items-end justify-content-end">
              <button
                className="btn btn-outline-secondary"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? "Exportando..." : "Exportar CSV"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      <PageState loading={loading} error={listError}>
        <DataTable<AuditLogItem>
          title="Logs de auditoria"
          rows={rows}
          total={total}
          page={page}
          take={take}
          onPageChange={setPage}
          columns={[
            { label: "Evento", render: (item) => item.event },
            { label: "Usuario", render: (item) => item.user?.name ?? "-" },
            { label: "Resource", render: (item) => item.resourceType ?? "-" },
            { label: "Resource ID", render: (item) => item.resourceId ?? "-" },
            { label: "Data", render: (item) => item.createdAt },
            {
              label: "Detalhe",
              render: (item) => (
                <button
                  className="btn btn-link btn-sm"
                  onClick={() => setSelected(item)}
                >
                  Ver
                </button>
              ),
            },
          ]}
        />
      </PageState>

      {selected && (
        <div className="card shadow-sm mt-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h5 className="card-title">Detalhe do log</h5>
                <p className="text-muted mb-0">Evento: {selected.event}</p>
              </div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setSelected(null)}
              >
                Fechar
              </button>
            </div>
            {selectedMail && (
              <div className="mt-3">
                <h6>Status do e-mail</h6>
                <div className="row g-2 small">
                  <div className="col-md-4">
                    <strong>Status:</strong> {String(selectedMail.status ?? "-")}
                  </div>
                  <div className="col-md-4">
                    <strong>MessageId:</strong> {String(selectedMail.messageId ?? "-")}
                  </div>
                  <div className="col-md-4">
                    <strong>Provider:</strong> {String(selectedMail.provider ?? "-")}
                  </div>
                  <div className="col-md-6">
                    <strong>Accepted:</strong> {formatList(selectedMail.accepted)}
                  </div>
                  <div className="col-md-6">
                    <strong>Rejected:</strong> {formatList(selectedMail.rejected)}
                  </div>
                  <div className="col-12">
                    <strong>Response:</strong> {String(selectedMail.response ?? "-")}
                  </div>
                </div>
                {selectedMailError && (
                  <div className="mt-3">
                    <h6>Erro do e-mail</h6>
                    <div className="row g-2 small">
                      <div className="col-md-4">
                        <strong>Code:</strong> {String(selectedMailError.code ?? "-")}
                      </div>
                      <div className="col-md-4">
                        <strong>Command:</strong> {String(selectedMailError.command ?? "-")}
                      </div>
                      <div className="col-md-4">
                        <strong>ResponseCode:</strong>{" "}
                        {String(selectedMailError.responseCode ?? "-")}
                      </div>
                      <div className="col-12">
                        <strong>Message:</strong> {String(selectedMailError.message ?? "-")}
                      </div>
                      <div className="col-12">
                        <strong>Response:</strong> {String(selectedMailError.response ?? "-")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="row g-3 mt-2">
              <div className="col-md-6">
                <h6>Before</h6>
                <pre className="bg-light p-3 rounded border small">
                  {JSON.stringify(selected.before ?? {}, null, 2)}
                </pre>
              </div>
              <div className="col-md-6">
                <h6>After</h6>
                <pre className="bg-light p-3 rounded border small">
                  {JSON.stringify(selected.after ?? {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
